import {
  Button,
  Checkbox,
  Grid,
  Image,
  Input,
  Modal,
  Select,
  Table,
  Text,
  TextInput,
  useMantineTheme
} from "@mantine/core";
import {
  IconDeviceFloppy,
  IconPrinter,
  IconReceipt,
} from "@tabler/icons-react";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getAllocationById } from "../../api/allocation";
import { getPono } from "../../api/poList";
import { saveFloorData } from "../../api/saveFloorData";
import { getStyleList } from "../../api/styleList";
import NavBar from "../../components/NavBar/NavBar";
import PoTable from "../../components/PoTable/PoTable";
import PrintStoreLabels from "../../components/Print/PrintStorLabels";
import { useDebounce } from "../../Hooks/useDebounce";
import { PoNoWithStatus } from "../../interface/AllocationInvoice";
import { allocationPaperPDF } from "../../PDFs/allocationPaper";
import { sizeAllocationPDF } from "../../PDFs/sizeAllocation";
import { DisplayStatus, FEATURE_FLAGS, getImageBase64 } from "../../Utils/utils";
import "../Global.css";
import classes from "./FloorUser.module.css";

const FloorUser: React.FC = () => {
  const [allocation, setAllocation] = useState<any>();
  const [allocationData, setAllocationData] = useState<any[]>([]);
  const [poNoList, setPoNoList] = useState<any[]>([]);
  const [styleList, setStyleList] = useState<any[]>([]);
  const [po, setPo] = useState<{ poNo: string; poId: string }>({
    poNo: "",
    poId: "",
  });
  const poSearch = useDebounce(po.poNo, 700);
  const token = useSelector((state: any) => state.user.sessionId);
  const [poTable, setPoTable] = useState<any[]>();
  const theme = useMantineTheme();
  const [printModal, setPrintModal] = useState<boolean>(false);
  const [single, setSingle] = useState(false);
  const [itemType, setItemType] = useState<"clothing" | "footwear">("clothing");
  const [orientation, setOrientation] = useState<"portrait" | "landscape">(
    "portrait"
  );
  const [imageData, setImageData] = useState<string>("");
  const [file, setFile] = useState<File | undefined>(undefined);
  const [viewModalOpened, setViewModalOpened] = useState<boolean>(false);
  const [viewImageModal, setViewImageModal] = useState<boolean>(false);
  const [sizeQty, setSizeQty] = useState<string>("");
  const [osBox, setOsBox] = useState(false);
  const [osBoxPacked, setOsBoxPacked] = useState<number[]>([]);
  const [boxQty, setBoxQty] = useState<number>();

  const getPo = async () => {
    const poList = await getPono(token, po?.poNo);
    if (poList) {
      setPoNoList(
        poList?.data?.map((item: PoNoWithStatus) => {
          return {
            label: item.poNo, // Use `item.poNo` for the label
            value: item.poId!.toString(), // Convert `poId` to string for the value
          };
        })
      );
    }
  };

  useEffect(() => {
    getPo();
  }, [poSearch]);

  const handlePoNoChange = async (value: string | null) => {
    if (value) {
      setPo({
        poId: value,
        poNo: poNoList.find((item: any) => item.value === value).label,
      });
      const styles = await getStyleList(token, parseInt(value));
      setAllocationData(styles);
      setPoTableData(styles);
      if (styles) {
        setSizeQty('');
        setStyleList(
          styles.map((item: any) => {
            return {
              label: item.style_no,
              value: item.style_id.toString(),
            };
          })
        );
      }
    }
  };

  // update the po table after the style is changed and fetch the latest list
  const updatePoTable = async () => {
    const currentPo = Number(poNoList.find((item: any) => item.label === po.poNo)?.value);
    const styles = await getStyleList(token, currentPo);
    setPoTableData(styles);
  }
  const handleStyleChange = async (value: string | null) => {
    if (value) {
      updatePoTable();
      const alloc = await getAllocationById(token, allocationData?.find((item: any) => item.style_id === parseInt(value))?.allocation_id);
      setAllocation(alloc);
      if (alloc?.osCaseQty?.length > 0) {
        setOsBox(true);
        setOsBoxPacked(alloc.osCaseQty);
      } else {
        setOsBox(false);
        setOsBoxPacked([]);
      }
      setSizeQty('');
      let image = await getImageBase64(
        `/uploads/${alloc?.image}`
      );
      setImageData(image as string);
    }
  };

  //Calculate OverstockPerSize after Changes
  const calculateOverstockPerSize = () => {
    if (allocation?.receivedQty && allocation?.totalAllocationPerSize) {
      const overstockPerSize: number[] = [];
      const length = Math.min(
        allocation.receivedQty.length,
        allocation.totalAllocationPerSize.length
      );
      for (let i = 0; i < length; i++) {
        let overstock =
          allocation.receivedQty[i].quantity -
          allocation.totalAllocationPerSize[i];
        overstockPerSize.push(overstock);
      }
      setAllocation((prevState: any) => ({
        ...prevState,
        overstockPerSize: overstockPerSize,
      }));
    }
  };

  const setPoTableData = async (data: any) => {
    setPoTable(
      data
        ?.map((item: any) => ({
          styleNo: item.style_no,
          status: item.allocation_status.slice(0, item.size_quantity_count).some((value: boolean) => value === false)
            ? "Pending"
            : "Done",
        }))
        .sort((a: any, b: any) => {
          const statusA = a.status.toLowerCase();
          const statusB = b.status.toLowerCase();
          if (statusA === statusB) return 0;
          return statusA === "pending" ? -1 : 1;
        })
    );
  };

  const handleInitialChange = async (e: string, storeIndex: number) => {
    if (e === null) return;
    setAllocation((prev: any) => ({
      ...prev,
      initial: prev.initial.map((val: string, idx: number) =>
        idx === storeIndex ? e : val
      ),
    }));
  };

  const handleQuantityChange = async (
    e: ChangeEvent<HTMLInputElement>,
    storeIndex: number,
    index: number
  ) => {
    const value = e.target.value || "0";
    let newSizeQuantities = allocation.sizeQuantities;
    let newOverstockPerSize = allocation.overstockPerSize;
    let newTotalAllocationPerSize = allocation.totalAllocationPerSize;
    newSizeQuantities[storeIndex][index].quantity = parseInt(value);
    newTotalAllocationPerSize[index] = parseInt(
      newSizeQuantities
        .map((item: any) => item[index].quantity)
        .reduce((a: number, b: number) => a + b, 0)
    );
    newOverstockPerSize[index] =
      allocation.receivedQty[index].quantity - newTotalAllocationPerSize[index];
    setAllocation({
      ...allocation,
      sizeQuantity: newSizeQuantities,
      totalAllocationPerSize: newTotalAllocationPerSize,
      overstockPerSize: newOverstockPerSize,
    });
    calculateOverstockPerSize();
  };

  const handleReceivedQtyChange = async (
    e: ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    let newReceivedQty = allocation.receivedQty;
    let newOverstockPerSize = allocation.overstockPerSize;
    newOverstockPerSize[index] =
      allocation.receivedQty[index].quantity -
      allocation.totalAllocationPerSize[index];
    newReceivedQty[index].quantity = parseInt(e.target.value);
    setAllocation({
      ...allocation,
      receivedQty: newReceivedQty,
      total: newReceivedQty.reduce(
        (a: number, b: { quantity: number }) => a + b.quantity,
        0
      ),
    });
    calculateOverstockPerSize();
  };

  const handleSave = async () => {
    if (allocation.color?.trim() === "" || allocation.color === null) {
      alert("Please mention color before saving.");
      return;
    }
    if (allocation?.initial?.some((item: string) => typeof item !== 'string' || item.trim() === '')) {
      alert('Please mention initial before saving.');
      return;
    }

    if (allocation?.overstockPerSize?.some((item: number) => item < 0)) {
      alert('Overstock per size cannot be negative.');
      return;
    }

    try {
      const saveData = await saveFloorData(token, {
        ...allocation, initial: allocation.initial,
        allocation_status: allocation.initial.map((item: string) => item === "SVP" ? false : true)
      });
      if (!saveData) {
        alert("Could not save the data");
        return;
      }
      alert("Data saved successfully.");
      if (saveData.data) {
        let newStatus = saveData.data.initial.some((value: string) => value === "SVP") ? "Pending" : "Done";
        await setPoTable(
          poTable?.map((item) => item.styleNo === saveData.data.style_no ? { ...item, status: newStatus } : item).sort((a, b) => {
            if (a.status === b.status) return 0;
            return a.status === 'Pending' ? -1 : 1;
          })
        );
        // set data in allocationData
        setAllocationData((prev: any) => prev.map((item: any) => item.style_id === saveData.data.style_id ?
          {
            ...item, sizeQuantities: saveData?.data?.sizeQuantities,
            description: saveData?.data?.description || '',
            color: saveData?.data?.color,
            initial: saveData?.data?.initial
          } : item))
      }
    } catch (error) {
      alert("Error saving data.");
      console.log("Error saving data");
    }
  };

  const downloadAllocationPaper = async () => {
    let sizes = allocation?.sizeQuantities[0]?.map(
      (item: { size: string }) => item.size
    );
    let tableData = allocation.storeName.map((store: string, index: number) => {
      return {
        storeName: store,
        sizeQuantities: allocation.sizeQuantities[index],
      };
    });
    let totalProductionQuantities = allocation.receivedQty.map(
      (item: { quantity: number }) => item.quantity
    );
    let totalProductionSum = totalProductionQuantities.reduce(
      (a: number, b: number) => a + b,
      0
    );
    let alloc = {
      binLocation: allocation.binlocation,
      description: allocation.description,
      color: allocation.color,
      cost: allocation.cost,
      msrp: allocation.msrp,
      notes: allocation.note,
      poId: allocation.po_id,
      poNo: allocation.po_no,
      styleNo: allocation.style_no,
      supplierName: allocation.supplierName,
    };
    await allocationPaperPDF(
      sizes,
      tableData,
      totalProductionQuantities,
      totalProductionSum,
      alloc,
      allocation.po_no
    );
    await sizeAllocationPDF(sizes, 6, tableData, allocation.style_no);
  };

  const handleFileChange = (files: File | null) => {
    if (files) {
      setFile(files);
    }
  };

  const handleImageUrlChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const url = e.target.value;
    const response = await fetch(url);
    const blob = await response.blob();
    const reader = new FileReader();
    reader.onload = () => {
      setImageData(reader.result as string);
      setFile(new File([blob], "image.jpg", { type: blob.type }));
    };
    reader.readAsDataURL(blob);
  };
  
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const message = "Are you sure you want to leave this page?";
      event.returnValue = message; // Standard for most browsers
      return message; // For some older browsers
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    // Cleanup the event listener when the component unmounts
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const ViewModal = () => {
    return (
      <Modal
        opened={viewModalOpened}
        onClose={() => setViewModalOpened(false)}
        title={`${allocation?.style_no}`}
        size={"xl"}
      >
        <div className={classes.viewModal}>
          <Image
            src={`/styleImages/${allocation?.image}`}
            alt={`${allocation?.style_no}`}
          />
        </div>
      </Modal>
    );
  };

  const handleOsCaseQtyChange = (index: number, value: number) => {
    let newOsBoxPacked = allocation?.osCaseQty;
    newOsBoxPacked[index].quantity = value;
    setAllocation({ ...allocation, osCaseQty: newOsBoxPacked });
  }

  const handleCheckOsBox = (value: boolean) => {
    if (value) {
      setOsBoxPacked(Array(allocation?.receivedQty?.length).fill(0))
      setAllocation({
        ...allocation, osCaseQty: allocation?.receivedQty?.map((item: any) => {
          return {
            size: item.size,
            quantity: 0
          }
        })
      });
    } else {
      setOsBoxPacked([]);
      setAllocation({ ...allocation, osCaseQty: null });
    }
    setOsBox(value)
  }

  return (
    <div>
      <NavBar />
      <ViewModal />
      <div>
        <div className={classes.main}>
          <Grid style={{ width: "100%" }}>
            <Grid.Col span={{ xs: 8, sm: 8, md: 8 }}>
              <div className={classes.poStyleDiv}>
                <div className={classes.gridRowDiv}>
                  <Text className={classes.textWithInput}>Po No:</Text>
                  <Select
                    data={poNoList}
                    placeholder="Select Po No"
                    style={{ flexGrow: 1 }}
                    searchable
                    clearable
                    onChange={handlePoNoChange}
                    onSearchChange={(e) =>
                      setPo({ ...po, poNo: e.toUpperCase() })
                    }
                    onClear={() => setPo({ poId: "", poNo: "" })}
                  />
                </div>
                <div className={classes.gridRowDiv}>
                  <Text className={classes.textWithInput}>Style No:</Text>
                  <Select
                    data={styleList}
                    placeholder="Select Style No"
                    style={{ flexGrow: 1 }}
                    searchable
                    clearable
                    onChange={handleStyleChange}
                  />
                </div>
                {allocation && (
                  <div className={classes.gridRowDiv}>
                    <Text className={classes.textWithInput}>Bin:</Text>
                    <TextInput
                      style={{ flexGrow: 1 }}
                      value={allocation?.binlocation || "N/A"}
                    />
                  </div>
                )}
                {(allocation?.orders || allocation?.order_status !== 'allocation' || allocation?.order_status !== null) && (
                  <div className={classes.gridRowDiv}>
                    <Text className={classes.textWithInput}>Info: </Text>
                    <Text className={classes.textWithInput} style={{ color: 'rosybrown' }}>{DisplayStatus[allocation?.order_status] || "N/A"} </Text>
                  </div>
                )}
              </div>
              {allocation && (
                <Grid>
                  <Grid.Col
                    span={{ xs: 6, sm: 4, md: 4, xl: 4 }}
                    className={classes.gridColDiv}
                  >
                    <div className={classes.gridRowDiv}>
                      <Text className={classes.textWithInput}>Supplier:</Text>
                      {/* <TextInput style={{ flexGrow: 1 }} value={allocation?.supplierName} onChange={(e) => setAllocation({ ...allocation, supplierName: e.target.value })} /> */}
                      <TextInput
                        style={{ flexGrow: 1 }}
                        value={allocation?.supplierName}
                      />
                    </div>
                  </Grid.Col>
                  <Grid.Col
                    span={{ xs: 6, sm: 4, md: 4, xl: 4 }}
                    className={classes.gridColDiv}
                  >
                    <div className={classes.gridRowDiv}>
                      <Text className={classes.textWithInput}>Notes:</Text>
                      <TextInput
                        variant="unstyled"
                        style={{
                          flexGrow: 1,
                          // backgroundColor: "#3492eb",
                          backgroundColor: "#FFEB3B",
                          border: "1px solid black",
                          borderRadius: "5px",
                        }}
                        value={allocation?.notes}
                        onChange={(e) =>
                          setAllocation({
                            ...allocation,
                            notes: e.target.value,
                          })
                        }
                      />
                    </div>
                  </Grid.Col>
                  <Grid.Col
                    span={{ xs: 6, sm: 4, md: 4, xl: 4 }}
                    className={classes.gridColDiv}
                  >
                    <div className={classes.gridRowDiv}>
                      <Text className={classes.textWithInput}>
                        Description:
                      </Text>
                      <TextInput
                        style={{ flexGrow: 1 }}
                        value={allocation?.description}
                        onChange={(e) =>
                          setAllocation({
                            ...allocation,
                            description: e.target.value,
                          })
                        }
                      />
                    </div>
                  </Grid.Col>
                  <Grid.Col
                    span={{ xs: 6, sm: 4, md: 4, xl: 4 }}
                    className={classes.gridColDiv}
                  >
                    <div className={classes.gridRowDiv}>
                      <Text className={classes.textWithInput}>Color:</Text>
                      <TextInput
                        style={{ flexGrow: 1 }}
                        value={allocation?.color}
                        onChange={(e) =>
                          setAllocation({
                            ...allocation,
                            color: e.target.value,
                          })
                        }
                      />
                    </div>
                  </Grid.Col>
                  <Grid.Col
                    span={{ xs: 6, sm: 4, md: 4, xl: 4 }}
                    className={classes.gridColDiv}
                  >
                    <div className={classes.gridRowDiv}>
                      <Text className={classes.textWithInput}>Cost:</Text>
                      <TextInput
                        style={{ flexGrow: 1 }}
                        value={allocation?.cost}
                      />
                    </div>
                  </Grid.Col>
                  <Grid.Col
                    span={{ xs: 6, sm: 4, md: 4, xl: 4 }}
                    className={classes.gridColDiv}
                  >
                    <div className={classes.gridRowDiv}>
                      <Text className={classes.textWithInput}>MSRP:</Text>
                      <TextInput
                        style={{ flexGrow: 1 }}
                        value={allocation?.msrp}
                      />
                    </div>
                  </Grid.Col>
                </Grid>
              )}
            </Grid.Col>
            <Grid.Col span={{ xs: 4, sm: 4, md: 4 }}>
              {poTable && (
                <div>
                  <div className={classes.info}>
                    {po.poNo && (
                      <div className={classes.infoMain}>
                        {/* <Text fw={650} className={classes.infoTitle} fz={'h4'}>PoNo:</Text> */}
                        <Text fw={550} className={classes.infoText} fz={"h4"}>
                          {po.poNo}
                        </Text>
                      </div>
                    )}
                    <Grid>
                      <Grid.Col span={6}>
                        <div className={classes.infoSub}>
                          <Text className={classes.infoTitle} fz={"h5"}>
                            Pending
                          </Text>
                          <Text className={classes.infoText} fz={"h5"}>
                            {
                              poTable.filter(
                                (item: any) => item.status === "Pending"
                              ).length
                            }
                          </Text>
                        </div>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <div className={classes.infoSub}>
                          <Text className={classes.infoTitle} fz={"h5"}>
                            Done
                          </Text>
                          <Text className={classes.infoText} fz={"h5"}>
                            {
                              poTable.filter(
                                (item: any) => item.status === "Done"
                              ).length
                            }
                          </Text>
                        </div>
                      </Grid.Col>
                    </Grid>
                  </div>
                  <div className={classes.poTable}>
                    <PoTable data={poTable} />
                  </div>
                </div>
              )}
            </Grid.Col>
          </Grid>
          {/* {allocation && (<Table withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th className={classes.rowDiv}>
                  <Text className={classes.textWithInput} style={{ width: '100px' }}>Qty OS:</Text>
                  <Checkbox className={classes.inputElement} checked={osBox} onChange={(e) => handleCheckOsBox(e.target.checked)} color={theme.colors.gray[9]} />
                </Table.Th>
                {osBoxPacked.map((item: any) => {
                  return <Table.Th>{item}</Table.Th>;
                })}
                <Table.Th></Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
          </Table>)} */}
          <div className={classes.mainTable}>
            {allocation && (
              <Table
                withTableBorder
                withColumnBorders
                stickyHeader
                striped
                className={classes.table}
              >
                <Table.Thead>
                  {FEATURE_FLAGS.OS_BOX_FLAG && <Table.Tr style={{ backgroundColor: theme.colors.gray[1] }}>
                    <Table.Th className={classes.rowDiv}>
                      <Text className={classes.textWithInput} style={{ width: '100px' }}>Qty OS:</Text>
                      <Checkbox variant="outline" className={classes.inputElement} checked={osBox} onChange={(e) => handleCheckOsBox(e.target.checked)} color={theme.colors.gray[9]} />
                    </Table.Th>
                    {allocation?.osCaseQty?.map((item: any, index: number) => {
                      return <Table.Th><TextInput type="number" value={item.quantity} onChange={(e) => handleOsCaseQtyChange(index, Number(e.target.value))} /></Table.Th>;
                    })}
                    {/* <Table.Th></Table.Th>
                    <Table.Th></Table.Th> */}
                  </Table.Tr>}
                  <Table.Tr className={classes.tableHead}>
                    <Table.Th className={classes.tableHead}>Store Name</Table.Th>
                    {allocation?.sizeQuantities[0]?.map((item: any) => {
                      return <Table.Th className={classes.tableHead}>{item.size}</Table.Th>;
                    })}
                    <Table.Th className={classes.tableHead}>Total</Table.Th>
                    <Table.Th className={classes.tableHead}>Initial</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  <Table.Tr className={classes.mainRows}>
                    <Table.Th>RCV QTY</Table.Th>
                    {allocation.receivedQty.map((item: any, index: number) => {
                      return (
                        <Table.Td>
                          <TextInput
                            type="number"
                            variant="unstyle"
                            value={item.quantity}
                            onChange={(e) => handleReceivedQtyChange(e, index)}
                            className={classes.tableData}
                          />
                        </Table.Td>
                      );
                    })}
                    <Table.Td>
                      <TextInput
                        type="number"
                        variant="unstyle"
                        value={allocation.receivedQty.reduce(
                          (a: number, b: { quantity: number }) =>
                            a + b.quantity,
                          0
                        )}
                      />
                    </Table.Td>
                    <Table.Td></Table.Td>
                  </Table.Tr>
                  {allocation?.storeName?.map((store: string) => {
                    const storeIndex = allocation.storeName.indexOf(store);
                    return (
                      <Table.Tr>
                        <Table.Th>{store}</Table.Th>
                        {allocation.sizeQuantities[storeIndex].map(
                          (item: any, index: number) => {
                            return (
                              <Table.Td>
                                <TextInput
                                  type="number"
                                  variant="unstyle"
                                  value={item.quantity}
                                  onChange={(e) =>
                                    handleQuantityChange(e, storeIndex, index)
                                  }
                                />
                              </Table.Td>
                            );
                          }
                        )}
                        <Table.Td>
                          {allocation.sizeQuantities[storeIndex].reduce(
                            (a: number, b: { quantity: number }) =>
                              a + b.quantity,
                            0
                          )}
                        </Table.Td>
                        <Table.Td>
                          <TextInput
                            variant="unstyle"
                            value={allocation.initial[storeIndex]}
                            onChange={(e) =>
                              handleInitialChange(e.target.value as string, storeIndex)
                            }
                          />
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                  <Table.Tr className={classes.mainRows}>
                    <Table.Th>Total Allocation</Table.Th>
                    {allocation.totalAllocationPerSize.map(
                      (total: number, index: number) => {
                        return (
                          <Table.Td>
                            <TextInput
                              type="number"
                              variant="unstyle"
                              value={total}
                            />
                          </Table.Td>
                        );
                      }
                    )}
                    <Table.Td>
                      {allocation.totalAllocationPerSize.reduce(
                        (a: number, b: number) => a + b,
                        0
                      )}
                    </Table.Td>
                    <Table.Td></Table.Td>
                  </Table.Tr>
                  <Table.Tr className={classes.mainRows}>
                    <Table.Th>Overstock</Table.Th>
                    {allocation.overstockPerSize.map(
                      (total: number, index: number) => {
                        return (
                          <Table.Td>
                            <TextInput
                              type="number"
                              variant="unstyle"
                              value={total}
                            />
                          </Table.Td>
                        );
                      }
                    )}
                    <Table.Td>
                      {allocation.overstockPerSize.reduce(
                        (a: number, b: number) => a + b,
                        0
                      ) || 0}
                    </Table.Td>
                    <Table.Td></Table.Td>
                  </Table.Tr>
                  {FEATURE_FLAGS.OS_BOX_FLAG && <Table.Tr className={classes.mainRowsHighlight}>
                    <Table.Th>OS Adjustments</Table.Th>
                    {allocation?.osCaseQty?.map(
                      (item: any, index: number) => {
                        return (
                          <Table.Td >
                            <Text variant="unstyle" >{item.quantity !== 0 && ((allocation?.overstockPerSize[index] % item.quantity) === 0 ? '' : `- ${allocation?.overstockPerSize[index] % item.quantity} / + ${Math.abs((allocation?.overstockPerSize[index] % item.quantity) - item.quantity)}`)}</Text>
                          </Table.Td>
                        );
                      }
                    )}
                    <Table.Td></Table.Td>
                    <Table.Td></Table.Td>
                  </Table.Tr>}
                </Table.Tbody>
              </Table>
            )}
          </div>
        </div>
        <div className={"footerDiv"}>
          <Button
            variant="outline"
            className={classes.bottomBtn}
            onClick={() => setPrintModal(true)}
          >
            <IconPrinter />
            Print labels
          </Button>
          <Button
            variant="outline"
            className={classes.bottomBtn}
            onClick={downloadAllocationPaper}
          >
            <IconReceipt />
            Allocation Paper
          </Button>
          <Button
            variant="outline"
            className={classes.bottomBtn}
            onClick={handleSave}
          >
            <IconDeviceFloppy />
            Save
          </Button>
        </div>
      </div>
      {/*Print Modal*/}
      <Modal
        opened={printModal}
        onClose={() => setPrintModal(false)}
        title={`Print Label Options`}
        centered
        size={"lg"}
      >
        <div>
          <Grid>
            <Grid.Col span={6} style={{ gap: "20px" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <Table withTableBorder withColumnBorders stickyHeader>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Th className={classes.tableHeadModal}>
                        Size/Qty
                      </Table.Th>
                      <Table.Td>
                        <TextInput
                          type="text"
                          variant="unstyled"
                          className={classes.infoText}
                          value={sizeQty}
                          onChange={(e) =>
                            setSizeQty(e.target.value.toUpperCase())
                          }
                        />
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Th className={classes.tableHeadModal}>
                        Orientation
                      </Table.Th>
                      <Table.Td>
                        <Select
                          data={["portrait", "landscape"]}
                          defaultValue={"portrait"}
                          value={orientation}
                          disabled={single}
                          onChange={(e) =>
                            setOrientation(e as "portrait" | "landscape")
                          }
                          placeholder="Select orientation"
                        />
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Th className={classes.tableHeadModal}>
                        Image
                      </Table.Th>
                      <Table.Td>
                        {" "}
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files) {
                              const file = e.target.files[0];
                              const reader = new FileReader();
                              reader.onload = () => {
                                if (reader.result) {
                                  setImageData(reader.result.toString());
                                  handleFileChange(file);
                                }
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                      </Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </div>
            </Grid.Col>
            <Grid.Col span={6}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <Table withTableBorder withColumnBorders stickyHeader>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Th className={classes.tableHeadModal}>
                        URL
                      </Table.Th>
                      <Table.Td>
                        <TextInput
                          type="text"
                          variant="unstyled"
                          className={classes.infoText}
                          onChange={handleImageUrlChange}
                        />
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Th className={classes.tableHeadModal}>
                        Type
                      </Table.Th>
                      <Table.Td>
                        <Select
                          data={[
                            { label: "Clothing", value: "clothing" },
                            {
                              label: "Footwear/Accessories",
                              value: "footwear",
                            },
                          ]}
                          defaultValue={"clothing"}
                          onChange={(e) =>
                            setItemType(e as "clothing" | "footwear")
                          }
                          placeholder="Select orientation"
                        />
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Th className={classes.tableHeadModal}>
                        Single page
                      </Table.Th>
                      <Table.Td>
                        <Checkbox
                          defaultChecked
                          checked={single}
                          color={theme.colors.gray[8]}
                          style={{ display: "flex", alignItems: "flex-end" }}
                          onChange={(e) => {
                            setSingle(e.target.checked);
                            setOrientation("portrait");
                          }}
                        />
                      </Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </div>
            </Grid.Col>
            <Grid.Col span={12}>
              <Table withTableBorder withColumnBorders stickyHeader>
                <Table.Tbody>
                  <Table.Tr>
                    <Table.Th className={classes.tableHeadModal}>
                      Packed Qty per box
                    </Table.Th>
                    <Table.Td>
                      <TextInput
                        placeholder='Quantity per box'
                        type='number'
                        variant="unstyled"
                        // value={allocation?.style_no}
                        style={{ flexGrow: 1 }}
                        onChange={(e) => setBoxQty(parseInt(e.target.value))}
                      />
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </Grid.Col>
          </Grid>
        </div>
        {allocation && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "20px",
              flexDirection: "row",
              gap: "20px",
            }}
          >
            <PrintStoreLabels
              props={{
                allocationData: {
                  storeName: allocation.storeName || [],
                  sizeQuantities: allocation.sizeQuantities,
                },
                styleData: {
                  color: allocation?.color || "",
                  cost: allocation?.cost || 0,
                  msrp: allocation?.msrp || 0,
                  description: allocation?.description || "",
                  supplier_name: allocation?.supplierName || "",
                  style_no: allocation?.style_no || "",
                  binlocation: allocation?.binlocation || "",
                  notes: allocation?.notes || "",
                },
                orientation,
                imageData,
                storeTotals: allocation?.sizeQuantities.map((item: any) => {
                  return item.reduce(
                    (a: number, b: { size: string; quantity: number }) =>
                      a + b.quantity,
                    0
                  );
                }),
                overstockTotal: allocation?.overstockPerSize.reduce(
                  (a: number, b: number) => a + b,
                  0
                ),
                styleNo: allocation.style_no || "",
                itemType,
                single,
                totalAllocation: allocation.totalAllocationPerSize.reduce(
                  (a: number, b: number) => a + b,
                  0
                ),
                file,
                manualsizeQty: sizeQty,
              }}
            />
            <PrintStoreLabels
              props={{
                allocationData: {
                  storeName: allocation.storeName || [],
                  sizeQuantities: allocation.sizeQuantities,
                },
                styleData: {
                  color: allocation?.color || "",
                  cost: allocation?.cost || 0,
                  msrp: allocation?.msrp || 0,
                  description: allocation?.description || "",
                  supplier_name: allocation?.supplierName || "",
                  style_no: allocation?.style_no || "",
                  binlocation: allocation?.binlocation || "",
                  notes: allocation?.notes || "",
                },
                orientation,
                imageData,
                storeTotals: allocation?.sizeQuantities.map((item: any) => {
                  return item.reduce(
                    (a: number, b: { size: string; quantity: number }) =>
                      a + b.quantity,
                    0
                  );
                }),
                overstockTotal: allocation?.overstockPerSize.reduce(
                  (a: number, b: number) => a + b,
                  0
                ),
                styleNo: allocation.style_no || "",
                itemType,
                single,
                totalAllocation: allocation.totalAllocationPerSize.reduce(
                  (a: number, b: number) => a + b,
                  0
                ),
                file,
                manualsizeQty: sizeQty,
                boxQty,
                btnLabel: "Store Label",
              }}
            />
            {allocation?.image && (
              <Button onClick={() => setViewImageModal(true)}>View Image</Button>
            )}
          </div>
        )}
      </Modal>
      <Modal
        opened={viewImageModal}
        onClose={() => setViewImageModal(false)}>
        <Image
          src={`uploads/${allocation?.image}`}
        />
      </Modal>
    </div>
  );
};

export default FloorUser;
